import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronRight } from 'lucide-react';
import { patientsApi } from '../lib/api';
import { format } from 'date-fns';

export default function PatientsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['patients', search, page],
    queryFn: () => patientsApi.list({ search: search || undefined, page, limit: 25 }),
    keepPreviousData: true,
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Patients</h1>
        <button onClick={() => navigate('/app/patients/new')} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Patient
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-clinical-muted" />
        <input
          type="text"
          className="input pl-9"
          placeholder="Search by name or patient number…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-clinical-border">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Patient</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">ID</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">DOB</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Gender</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Phone</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-clinical-border">
            {isLoading ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-clinical-muted">Loading…</td></tr>
            ) : data?.patients?.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-clinical-muted">No patients found</td></tr>
            ) : data?.patients?.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/app/patients/${p.id}`)}
                className="hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <td className="px-5 py-3 font-medium text-slate-800">
                  {p.lastName}, {p.firstName}
                </td>
                <td className="px-5 py-3 text-clinical-muted font-mono">{p.patientNumber}</td>
                <td className="px-5 py-3 text-clinical-muted">
                  {p.dateOfBirth ? format(new Date(p.dateOfBirth), 'dd MMM yyyy') : '—'}
                </td>
                <td className="px-5 py-3 text-clinical-muted capitalize">{p.gender || '—'}</td>
                <td className="px-5 py-3 text-clinical-muted">{p.phone || '—'}</td>
                <td className="px-5 py-3 text-clinical-muted">
                  <ChevronRight className="w-4 h-4" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {data?.total > 25 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-clinical-border">
            <p className="text-sm text-clinical-muted">
              Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, data.total)} of {data.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * 25 >= data.total}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
